import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    this.details.open = false;
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      this.details.open = false;
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}


// PS anchor-linked accordion support.
// A link such as #easytoclean opens the matching accordion,
// scrolls to it, changes the ENTIRE accordion item background,
// and makes all accordion text/icons white.

const PS_ACCORDION_ANCHOR_SELECTOR =
  'details[data-ps-accordion-anchor="true"][id]';

const PS_ACCORDION_ACTIVE_CLASS =
  'ps-accordion-anchor-active';


function psGetAccordionAnchorTarget(hash = window.location.hash) {
  if (!hash || hash === '#') return null;

  let id = hash.slice(1);

  try {
    id = decodeURIComponent(id);
  } catch {
    // Keep raw hash if decoding fails.
  }

  if (!id) return null;

  const target = document.getElementById(id);

  if (!(target instanceof HTMLDetailsElement)) return null;

  if (!target.matches(PS_ACCORDION_ANCHOR_SELECTOR)) return null;

  return target;
}


function psClearAccordionAnchorHighlights(except = null) {
  document
    .querySelectorAll(
      `${PS_ACCORDION_ANCHOR_SELECTOR}.${PS_ACCORDION_ACTIVE_CLASS}`
    )
    .forEach((details) => {

      if (details === except) return;

      details.classList.remove(PS_ACCORDION_ACTIVE_CLASS);

      const accordionItem = details.closest('accordion-custom');

      if (accordionItem) {
        accordionItem.style.removeProperty('background');
        accordionItem.style.removeProperty('background-color');
        accordionItem.style.removeProperty('color');

        accordionItem
          .querySelectorAll('*')
          .forEach((element) => {
            element.style.removeProperty('color');
            element.style.removeProperty('fill');
            element.style.removeProperty('stroke');
          });
      }
    });
}


function psOpenAccordionAnchor(hash, { scroll = true } = {}) {
  const target = psGetAccordionAnchorTarget(hash);

  if (!target) return false;

  psClearAccordionAnchorHighlights(target);

  target.open = true;

  target.classList.add(PS_ACCORDION_ACTIVE_CLASS);

  const accordionItem = target.closest('accordion-custom');

  const highlightColor =
    target.dataset.psAnchorHighlightColor || '#ff0000';

  if (accordionItem) {

    // Change the ENTIRE rounded accordion item.
    accordionItem.style.setProperty(
      'background',
      highlightColor,
      'important'
    );

    accordionItem.style.setProperty(
      'background-color',
      highlightColor,
      'important'
    );

    // Make text/icons white.
    accordionItem.style.setProperty(
      'color',
      '#ffffff',
      'important'
    );

    accordionItem
      .querySelectorAll('*')
      .forEach((element) => {

        element.style.setProperty(
          'color',
          '#ffffff',
          'important'
        );

        element.style.setProperty(
          'fill',
          'currentColor',
          'important'
        );

        element.style.setProperty(
          'stroke',
          'currentColor',
          'important'
        );
      });
  }

  if (scroll) {
    requestAnimationFrame(() => {

      const scrollTarget =
        accordionItem || target;

      scrollTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

    });
  }

  return true;
}


function psHandleAccordionAnchorClick(event) {
  const origin = event.target;

  if (!(origin instanceof Element)) return;

  const link = origin.closest('a[href]');

  if (!(link instanceof HTMLAnchorElement)) return;

  const url = new URL(
    link.href,
    window.location.href
  );

  if (!url.hash) return;

  if (
    url.origin !== window.location.origin ||
    url.pathname !== window.location.pathname ||
    url.search !== window.location.search
  ) {
    return;
  }

  if (!psGetAccordionAnchorTarget(url.hash)) return;

  event.preventDefault();

  if (window.location.hash !== url.hash) {
    history.pushState(
      null,
      '',
      url.hash
    );
  }

  psOpenAccordionAnchor(url.hash);
}


function psInitAccordionAnchors() {

  document.addEventListener(
    'click',
    psHandleAccordionAnchorClick
  );

  window.addEventListener(
    'hashchange',
    () => {
      psOpenAccordionAnchor(
        window.location.hash
      );
    }
  );

  if (window.location.hash) {

    psOpenAccordionAnchor(
      window.location.hash,
      { scroll: false }
    );

    requestAnimationFrame(() => {
      psOpenAccordionAnchor(
        window.location.hash
      );
    });
  }
}


psInitAccordionAnchors();